import { useEffect, useState, useSyncExternalStore } from 'react';
import { Location } from '../types';
import { calculateQiblaDirection } from '../utils/qibla';
import { isSafariExtension } from '../utils/browserApi';
import { bridge, bridgeErrorCode, isNativeApp } from '../utils/nativeApp';
import { trackFirstRun } from '../services/analytics';
import { saveToStorageAsync } from '../services/storage';
import i18n from '../i18n';

interface LocationHookResult {
  userLocation: Location | null;
  cityName: string | null;
  qiblaDirection: number | null;
  locationError: string | null;
  /**
   * True while a cold first-run geolocation prompt is held behind the location
   * primer (design spec §2.6). Shared across every mounted useLocation() via a
   * module store, so the six consumers that mount within ~300ms of load agree
   * on one answer and exactly one primer is rendered.
   */
  primerNeeded: boolean;
}

// Last known location, shared by every new-tab page. Without it, Safari (which
// does not persist geolocation grants for extension origins) re-prompts on every
// single new tab. Same pattern as the qibla_mag_declination cache.
const LOCATION_CACHE_KEY = 'rhemn_last_location';
const LOCATION_CACHE_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000; // hard expiry
const LOCATION_REFRESH_AGE_MS = 60 * 60 * 1000; // silent refresh past this age

interface CachedLocation {
  latitude: number;
  longitude: number;
  cityName: string | null;
  timestamp: number;
}

function readLocationCache(): CachedLocation | null {
  try {
    const raw = localStorage.getItem(LOCATION_CACHE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (
      typeof data?.latitude !== 'number' ||
      typeof data?.longitude !== 'number' ||
      typeof data?.timestamp !== 'number' ||
      Date.now() - data.timestamp > LOCATION_CACHE_MAX_AGE_MS
    ) return null;
    return data as CachedLocation;
  } catch {
    return null;
  }
}

function writeLocationCache(latitude: number, longitude: number, cityName: string | null, timestamp: number) {
  try {
    localStorage.setItem(LOCATION_CACHE_KEY, JSON.stringify({ latitude, longitude, cityName, timestamp }));
  } catch {
    // storage full/unavailable — next tab just re-resolves
  }
}

// OpenWeather reverse geocoding — same domain/key as weather, already allowed by the manifest CSP
async function requestCityName(lat: number, lon: number): Promise<string | null> {
  try {
    const apiKey = process.env.REACT_APP_WEATHER_API_KEY;
    if (!apiKey) return null;
    const res = await fetch(
      `https://api.openweathermap.org/geo/1.0/reverse?lat=${lat}&lon=${lon}&limit=1&appid=${apiKey}`,
      { signal: AbortSignal.timeout(6000) },
    );
    if (!res.ok) return null;
    const data = await res.json();
    const place = data?.[0];
    if (!place?.name) return null;
    return place.state ? `${place.name}, ${place.state}` : place.name;
  } catch {
    return null;
  }
}

// ── Reverse-geocode single-flight ─────────────────────────────────────────────
// The same six consumers each resolve a city for the same fix: on native that
// was six identical /geo/1.0/reverse requests per launch (Sentry
// REHMN-EXTENSION-E). Keyed by the fix rounded to 3 decimals (~110 m), the
// burst shares one request and later mounts reuse its answer. A null (no key,
// network failure, no match) is NOT memoized, so a later mount retries.
const cityKey = (lat: number, lon: number): string => `${lat.toFixed(3)},${lon.toFixed(3)}`;
const cityNameInFlight = new Map<string, Promise<string | null>>();
let cityNameMemo: { key: string; name: string } | null = null;

function fetchCityName(lat: number, lon: number): Promise<string | null> {
  const key = cityKey(lat, lon);
  if (cityNameMemo?.key === key) return Promise.resolve(cityNameMemo.name);
  let inFlight = cityNameInFlight.get(key);
  if (!inFlight) {
    inFlight = requestCityName(lat, lon)
      .then((name) => {
        if (name) cityNameMemo = { key, name };
        return name;
      })
      .finally(() => {
        cityNameInFlight.delete(key);
      });
    cityNameInFlight.set(key, inFlight);
  }
  return inFlight;
}

/** Test-only: clear the reverse-geocode single-flight/memo between test cases. */
export function __resetCityNameSharingForTests(): void {
  cityNameInFlight.clear();
  cityNameMemo = null;
}

// iOS native app: CoreLocation over the bridge (protocol §5 `location.get`) —
// reuses the host's App Group location cache and shows the real app name in the
// permission prompt. DENIED surfaces as an error (matching the Safari native
// path below); any other failure resolves null so the caller falls through to
// the local cache — never to web geolocation on native (fromWebGeolocation
// hard-guards it). Dead code off-native: isNativeApp() short-circuits before
// any bridge call.
type NativeFix = { coords: Location; fixTimestamp?: number } | { denied: string } | null;

async function getNativeAppPosition(): Promise<NativeFix> {
  if (!isNativeApp()) return null;
  try {
    const data = await bridge.call<{ latitude: number; longitude: number; timestamp?: number }>(
      'location.get',
      { maxAgeSeconds: 900 },
    );
    if (typeof data?.latitude === 'number' && typeof data?.longitude === 'number') {
      return {
        coords: { latitude: data.latitude, longitude: data.longitude },
        // Protocol leaves the timestamp unit unstated; the same Swift App Group
        // cache backs the Safari path (epoch ms), so it is treated as epoch ms.
        fixTimestamp: typeof data.timestamp === 'number' ? data.timestamp : undefined,
      };
    }
    return null;
  } catch (err) {
    if (bridgeErrorCode(err) === 'DENIED') return { denied: 'Location access denied' };
    return null; // UNAVAILABLE / FAILED / timeout → cached-location fallback (web geolocation is off-limits on native)
  }
}

// ── Native single-flight ──────────────────────────────────────────────────────
// Six consumers mount useLocation() within ~300ms of Home load (shell, prayer
// times, weather ×2, qibla ×2). The Swift host single-flights `location.get`
// and rejects concurrent calls with FAILED, so without JS-side sharing calls
// 2–6 lose the race and degrade to cache/error on a cold start. One
// module-level in-flight promise plus a short memo of the settled fix makes
// the whole burst cost exactly one bridge call. Successes and denials are
// memoized (a denial won't flip within seconds and must not re-trigger the
// host); a null (bridge hiccup) is deliberately NOT memoized so a later mount
// gets a fresh attempt. Dead code off-native.
const NATIVE_FIX_MEMO_MS = 30_000;
let nativeFixInFlight: Promise<NativeFix> | null = null;
let nativeFixMemo: { fix: NativeFix; at: number } | null = null;

function getNativeAppPositionShared(): Promise<NativeFix> {
  if (!isNativeApp()) return Promise.resolve(null);
  if (nativeFixMemo && Date.now() - nativeFixMemo.at < NATIVE_FIX_MEMO_MS) {
    return Promise.resolve(nativeFixMemo.fix);
  }
  if (!nativeFixInFlight) {
    nativeFixInFlight = getNativeAppPosition()
      .then((fix) => {
        if (fix !== null) nativeFixMemo = { fix, at: Date.now() };
        return fix;
      })
      .finally(() => {
        nativeFixInFlight = null;
      });
  }
  return nativeFixInFlight;
}

/** Test-only: clear the single-flight/memo module state between test cases. */
export function __resetNativeLocationSharingForTests(): void {
  nativeFixInFlight = null;
  nativeFixMemo = null;
}

// On Safari, navigator.geolocation prompts with "(null)" as the requester (WebKit
// renders the opaque safari-web-extension:// origin). Ask the native app layer via
// sendNativeMessage instead — CoreLocation's prompt shows the real extension name.
// Resolves null when unavailable (old appex, non-Safari) so callers can fall back.
async function getNativePosition(): Promise<NativeFix> {
  try {
    const runtime = (globalThis as any).browser?.runtime;
    if (!isSafariExtension() || typeof runtime?.sendNativeMessage !== 'function') return null;
    // Race a timeout so a hung native handler can never freeze the UI
    const res: any = await Promise.race([
      runtime.sendNativeMessage('application.id', { type: 'getLocation' }),
      new Promise((resolve) => setTimeout(() => resolve(null), 20000)),
    ]);
    if (typeof res?.latitude === 'number' && typeof res?.longitude === 'number') {
      return {
        coords: { latitude: res.latitude, longitude: res.longitude },
        // Real fix age from the native cache (ms) — keeps our local cache honest
        fixTimestamp: typeof res.timestamp === 'number' ? res.timestamp : undefined,
      };
    }
    if (res?.error === 'denied') return { denied: 'Location access denied' };
    // "notDetermined" (containing app hasn't asked yet), timeout, old appex echo →
    // fall back to web geolocation
    return null;
  } catch {
    return null;
  }
}

// ── First-run location primer gate (design spec §2.6) ─────────────────────────
// A cold client used to fire trackFirstRun('geo_shown') and getCurrentPosition
// back to back — an OS dialog with no reason attached, over an app that had not
// yet shown the user anything. The gate holds that call until the primer's
// Continue tap, so the OS prompt is raised by a user gesture and the primer is
// one sentence in FRONT of the real permission system, not a second one.
//
// It lives at module scope on purpose: six consumers mount useLocation() within
// ~300ms of load and each would otherwise reach the cold path independently.
// One armed gate, one primer, one release that drains every waiter.
const PRIMER_DEFERRED_KEY = 'location.primerDeferred';

let primerArmed = false;
let primerReleased = false;
const primerWaiters = new Set<() => void>();
const primerListeners = new Set<() => void>();

function primerSnapshot(): boolean {
  return primerArmed && !primerReleased;
}

function notifyPrimer(): void {
  primerListeners.forEach((fn) => fn());
}

function subscribePrimer(onChange: () => void): () => void {
  primerListeners.add(onChange);
  return () => { primerListeners.delete(onChange); };
}

/** SSR/prerender snapshot. There is no browser geolocation to hold, so the
 *  primer is never needed server-side; useSyncExternalStore requires this to
 *  be referentially stable, and a literal false is. */
function primerSnapshotServer(): boolean {
  return false;
}

/**
 * "Not now" was tapped on this device, ever. Read synchronously from
 * localStorage (the primary store — saveToStorageAsync writes it before its
 * first await) because the cold path has to decide before it prompts.
 */
function isPrimerDeferred(): boolean {
  try {
    return localStorage.getItem(PRIMER_DEFERRED_KEY) === 'true';
  } catch {
    return false;
  }
}

/**
 * Continue: release every held cold path. Called from the primer's button, so
 * getCurrentPosition runs inside the user gesture that asked for it.
 */
export function requestLocation(): void {
  if (primerReleased) return;
  primerReleased = true;
  const waiters = [...primerWaiters];
  primerWaiters.clear();
  notifyPrimer();
  waiters.forEach((run) => run());
}

/**
 * Not now: persist the deferral and never raise the primer on this device
 * again. The OS prompt is NOT fired, now or on a later tab — that is the point;
 * the deferred cold path in fromWebGeolocation reports an error instead.
 */
export function deferLocationPrimer(): void {
  primerWaiters.clear();
  primerArmed = false;
  try {
    localStorage.setItem(PRIMER_DEFERRED_KEY, 'true');
  } catch {
    /* storage unavailable — worst case the primer offers itself once more */
  }
  void saveToStorageAsync(PRIMER_DEFERRED_KEY, true);
  trackFirstRun('geo_primer_skipped');
  notifyPrimer();
}

/** Test-only: reset the module-level primer gate between cases. */
export function __resetLocationPrimerForTests(): void {
  primerArmed = false;
  primerReleased = false;
  primerWaiters.clear();
}

export const useLocation = (): LocationHookResult => {
  const [userLocation, setUserLocation] = useState<Location | null>(null);
  const [cityName, setCityName] = useState<string | null>(null);
  const [qiblaDirection, setQiblaDirection] = useState<number | null>(null);
  const [locationError, setLocationError] = useState<string | null>(null);

  const primerNeeded = useSyncExternalStore(subscribePrimer, primerSnapshot, primerSnapshotServer);

  useEffect(() => {
    let cancelled = false;

    const applyPosition = async (
      latitude: number,
      longitude: number,
      opts?: { knownCity?: string | null; fixTimestamp?: number },
    ) => {
      if (cancelled) return;
      setUserLocation({ latitude, longitude });
      setQiblaDirection(calculateQiblaDirection(latitude, longitude));
      const name = opts?.knownCity ?? (await fetchCityName(latitude, longitude));
      if (cancelled) return;
      // Fall back to coordinates so the UI never sits on "Locating…" once we have a fix
      setCityName(name ?? `${latitude.toFixed(2)}°, ${longitude.toFixed(2)}°`);
      // Preserve the original fix time on cache replays so the cache still expires
      writeLocationCache(latitude, longitude, name, opts?.fixTimestamp ?? Date.now());
    };

    const fromWebGeolocation = async (reportErrors: boolean): Promise<void> => {
      // iOS native app: NEVER touch navigator.geolocation — WKWebView would
      // raise its own WebKit permission prompt on top of the CoreLocation one
      // the bridge already owns (double-prompt observed by the Swift side).
      // Reaching here on native means the bridge failed non-DENIED and the
      // 7-day local cache is empty, so surface a calm error instead. This is
      // the single choke point: every web-geolocation call goes through it.
      if (isNativeApp()) {
        if (reportErrors) setLocationError('Location is currently unavailable.');
        return;
      }
      if (!navigator.geolocation) {
        if (reportErrors) setLocationError('Geolocation is not supported by this browser.');
        return;
      }
      // First-session funnel: reportErrors=true is the cold path (no cache, no
      // prior grant) where a permission prompt is what the user actually sees.
      // trackFirstRun is a no-op after first fire / for pre-instrumentation
      // clients, so the background-refresh path (reportErrors=false, grant
      // already held) never mislabels a silent refresh as a prompt.
      const prompt = () => {
        if (cancelled) return;
        if (reportErrors) trackFirstRun('geo_shown');
        navigator.geolocation.getCurrentPosition(
          (position) => {
            trackFirstRun('geo_granted');
            applyPosition(position.coords.latitude, position.coords.longitude);
          },
          (error) => {
            console.error('Error getting location:', error);
            if (error.code === error.PERMISSION_DENIED) trackFirstRun('geo_denied');
            if (!cancelled && reportErrors) setLocationError(error.message);
          },
          { timeout: 15000, maximumAge: 10 * 60 * 1000 },
        );
      };

      // Background refresh (grant already held) or the primer already released
      // on this page: nothing stands in front of the dialog.
      if (!reportErrors || primerReleased) {
        prompt();
        return;
      }

      // "Not now" was tapped on this device: no primer, and no OS dialog
      // either. The primer's footnote promised prayer times and Qibla stay
      // off; a prompt on the next cold tab would make that a lie.
      if (isPrimerDeferred()) {
        setLocationError(i18n.t('common:qibla.locationUnavailable'));
        return;
      }

      // The primer explains a dialog the user has not seen yet. When the grant
      // already exists (the 7-day cache expired on an idle device) there is no
      // dialog to explain, and when the browser-level answer is "denied" none
      // will come — hold only when the OS would actually ask.
      let permissionState: string | null = null;
      try {
        const perm = await (navigator as any).permissions?.query({ name: 'geolocation' });
        permissionState = perm?.state ?? null;
      } catch {
        // no Permissions API — assume it will ask
      }
      if (cancelled) return;
      if (permissionState === 'granted' || permissionState === 'denied') {
        prompt();
        return;
      }

      // Cold path (no cache, no prior answer): hold the OS prompt behind the
      // primer. `geo_shown` deliberately stays welded to the REAL prompt above,
      // or the funnel would silently change meaning mid-cohort (spec §2.6).
      heldPrompt = prompt;
      primerWaiters.add(prompt);
      if (!primerArmed) {
        primerArmed = true;
        trackFirstRun('geo_primer_shown');
      }
      notifyPrimer();
    };

    // The prompt this instance is holding behind the primer, if any — removed
    // on unmount so a released gate never calls into a dead closure.
    let heldPrompt: (() => void) | null = null;

    (async () => {
      // iOS native app first (null everywhere else), then the Safari appex path.
      const native = (await getNativeAppPositionShared()) ?? (await getNativePosition());
      if (cancelled) return;
      if (native && 'coords' in native) {
        // Native (CoreLocation) path: we can't observe the prompt itself, but a
        // first-ever grant/denial implies one was shown — record both so the
        // funnel's shown/granted stages stay comparable across platforms.
        // Once-ever guards make this a no-op on every later tab.
        trackFirstRun('geo_shown');
        trackFirstRun('geo_granted');
        const { latitude, longitude } = native.coords;
        // Same place as the last fix: its city is already known.
        const cached = readLocationCache();
        const knownCity = cached && cityKey(cached.latitude, cached.longitude) === cityKey(latitude, longitude)
          ? cached.cityName
          : null;
        applyPosition(latitude, longitude, { knownCity, fixTimestamp: native.fixTimestamp });
        return;
      }
      if (native && 'denied' in native) {
        trackFirstRun('geo_shown');
        trackFirstRun('geo_denied');
        setLocationError(native.denied);
        return;
      }

      const cached = readLocationCache();
      if (cached) {
        applyPosition(cached.latitude, cached.longitude, {
          knownCity: cached.cityName,
          fixTimestamp: cached.timestamp,
        });
        // Refresh in the background ONLY when the grant already exists — never
        // re-prompt a tab that can already render from cache. Skipped on native:
        // the web-geolocation grant is meaningless there (the bridge already
        // refreshed with maxAgeSeconds — if it failed, a WebKit query won't help).
        if (!isNativeApp() && Date.now() - cached.timestamp > LOCATION_REFRESH_AGE_MS) {
          try {
            const perm = await (navigator as any).permissions?.query({ name: 'geolocation' });
            if (!cancelled && perm?.state === 'granted') void fromWebGeolocation(false);
          } catch {
            // no Permissions API — keep the cache, avoid re-prompting
          }
        }
        return;
      }

      await fromWebGeolocation(true);
    })();

    return () => {
      cancelled = true;
      // Drop this instance's held prompt: its setState calls are already fenced
      // by `cancelled`, but leaving a dead closure in the waiter set is a leak.
      if (heldPrompt) primerWaiters.delete(heldPrompt);
    };
  }, []);

  return { userLocation, cityName, qiblaDirection, locationError, primerNeeded };
};
